import type {ActionResponse, Department, Identified, Location} from 'src/typings';
import type {IChangesObject, IComponentOptions} from 'angular';

export enum DataDisplayerOptions {
    Edit,
    Delete,
}

export interface DataDisplayerSettings {
    options: DataDisplayerOptions[];
    loadAll: () => Promise<Identified<number | string>[]>;
    columnsSetting: ColumnsSetting[];
}

export interface ColumnsSetting {
    title: string;
    key: string;
    valueModifier: (value) => any;
}

export interface ViewableDepartment extends Department {
    parent: Department;
    locations: Location[];
}

export type TComponentBindings = '@' | '<' | '&' | '=' | '@?' | '<?' | '&?' | '=?';

export interface ComponentController<T> {
    prototype: [T] extends [never] ? any : T;
}

export interface Component<BINDINGS> extends Omit<IComponentOptions, 'bindings' | 'controller'> {
    bindings?: {
        [prop in keyof BINDINGS]: TComponentBindings;
    };

    controller?: ComponentController<BINDINGS>;
}

export type OnChangesObject<BINDINGS> = {
    [property in keyof BINDINGS]: IChangesObject<BINDINGS[property]>;
};

export interface OnChanges<BINDINGS> {
    $onChanges(onChangesObj: OnChangesObject<BINDINGS>): void;
}

export interface PayloadActionResponse<T> extends ActionResponse {
    payload?: T;
}

export type AnyObject = Record<string, unknown>; // replacement for {}, object, Object

export type EmptyObject = Record<string, never> // replacement for {} (empty)

export type EnumValues<T> = T[keyof T];

export type TypeOrIdentifiedStub<TYPE> = TYPE extends Identified<infer ID> ? TYPE | Identified<ID> : TYPE;

export type AllowIdentifiedStubs<T extends Record<string, any>> = {
    [K in keyof T]: TypeOrIdentifiedStub<T[K]>;
};

export type BasicPrimitiveType = string | number | bigint | boolean | symbol;

export type NonNullableProperties<T> = {
    [P in keyof T]: NonNullable<T[P]>;
}

export type RequiredNonNullableProperties<T> = {
    [P in keyof T]-?: NonNullable<T[P]>;
}

// JSON.stringify looks for this method on object during serialization
export interface CustomJsonSerializer {
    toJSON(): any;
}

export interface Equatable {
    equals(other: any): boolean;
}

export type Defined<Type> = Type extends undefined ? never : Type;
export type NotNull<Type> = Type extends null ? never : Type;